/* eslint-disable @typescript-eslint/ban-ts-comment */
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { ISelectValue } from 'constants/globalTypes'
import { getSingleCycle } from 'features/cycles/cyclesSlice'
import { components } from 'pages/TaskTemplates/taskData'
import React from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Flex, ItemsGrid, ThinSeparator } from 'styling/GlobalStyles'
import { OtherText } from 'styling/Typography'
import { ICycleSingleResponse } from 'types/api/taskCyclesApi.interface'
import { IPhaseState, ITaskCycleBindingConfigState } from 'types/operationPlans.interface'
import { cantKeepUpOptions } from '../../planWording'

type CycleTemplateTypeProps = {
    formKey: string | number
    addingPhase: IPhaseState<ITaskCycleBindingConfigState>
    setAddingPhase: React.Dispatch<React.SetStateAction<IPhaseState<ITaskCycleBindingConfigState>>>
}

export const CycleTemplateType = ({ formKey, addingPhase, setAddingPhase }: CycleTemplateTypeProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const bindingConfig = addingPhase.bindingConfig ?? ({} as ITaskCycleBindingConfigState)

    const parse = (data: any) => {
        return data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : []
    }

    const updateBindingConfig = (patch: Partial<ITaskCycleBindingConfigState>) => {
        setAddingPhase((prev) => ({
            ...prev,
            bindingConfig: { ...(prev.bindingConfig ?? {}), ...patch } as ITaskCycleBindingConfigState,
        }))
    }

    const handleCycleSelect = async (value: ISelectValue) => {
        const resp = await dispatch(getSingleCycle(value.value.toString()))
        if (resp.meta.requestStatus === 'fulfilled') {
            setAddingPhase((prev) => ({
                ...prev,
                taskCycleTemplate: { label: value.label, value: value.value },
                bindingConfig: {
                    startDate: null,
                    parallelismTarget: null,
                    componentsData: (resp.payload as ICycleSingleResponse).components.map((component) => ({
                        taskCycleTemplateComponent: { label: component.name, value: component.id },
                        componentType: component.component,
                        value: null,
                    })),
                },
            }))
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateBindingConfig({ [e.target.name]: e.target.value } as Partial<ITaskCycleBindingConfigState>)
    }

    const handleComponentInput = (e: React.ChangeEvent<HTMLInputElement>, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        const newValue = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.taskCycleTemplateComponent?.value === componentId ? { ...c, value: newValue } : c,
            ),
        })
    }

    const handleComponentSelect = (value: ISelectValue, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.taskCycleTemplateComponent?.value === componentId ? { ...c, value } : c,
            ),
        })
    }

    const handleComponentDate = (date: Date, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.taskCycleTemplateComponent?.value === componentId ? { ...c, value: date } : c,
            ),
        })
    }

    const renderComponentInput = (component: IComponent) => {
        const foundRenderInput = components.find(c => c.component === component.componentType)

        let value
        const componentId = component.taskCycleTemplateComponent?.value
        const fn = foundRenderInput?.inputType == 3 ? handleComponentDate : [4, 5].includes(foundRenderInput?.inputType) ? handleComponentSelect : handleComponentInput

        switch (foundRenderInput?.inputType) {
            case 1:
                value = component.value
                break
            case 2:
                value = Number(component.value)
                break
            case 3:
                value = new Date(String(component.value))
                break
            case 4:
                value = (component.value as ISelectValue)?.value
                break
            case 5:
                value = (component.value as ISelectValue)?.label
        }

        return foundRenderInput?.renderInput({ handleChange: (e) => fn(e, componentId), value, name: 'value', labelText: component?.taskCycleTemplateComponent?.label })
    }

    return <>
        <Flex column gap='1rem' >
            <Flex column width='100%'>
                <HonestSelectAsyncInput
                    key={`cycle-template-${formKey}`}
                    keyProp={1}
                    defaultValue={addingPhase.taskCycleTemplate?.label}
                    link='task/cycle/template'
                    labelText='Pick the work template'
                    onChange={handleCycleSelect}
                    name='taskCycleTemplate'
                    placeholder='Search templates'
                    parserFunction={parse}
                    required
                    invalid={!addingPhase.taskCycleTemplate?.value}
                />
                <OtherText grey>Pre-built by HonestDig — pick one and fill in the blanks</OtherText>
            </Flex>
            {addingPhase.taskCycleTemplate?.value && <>
                <Flex gap='1rem' >
                    <CalendarInput
                        key={`cycle-startDate-${formKey}`}
                        labelText='Start Date'
                        minDate={new Date()}
                        name='startDate'
                        defaultValue={bindingConfig.startDate ?? null}
                        onChange={(date) => updateBindingConfig({ startDate: date })}
                        width='100%'
                    />
                    <Flex column width='100%'>
                        <Input
                            width='100%'
                            value={bindingConfig.parallelismTarget ?? ''}
                            placeholder='4'
                            labelText='How many run at once?'
                            name='parallelismTarget'
                            onChange={handleChange}
                            nomarg
                            type='number'
                        />
                        <OtherText grey>e.g. 4 = keep four trucks cycling</OtherText>
                    </Flex>
                    <Flex column width='100%'>
                        <Input
                            width='100%'
                            value={bindingConfig.weight ?? ''}
                            placeholder='Enter priority'
                            labelText='Priority when resources are short'
                            name='weight'
                            onChange={handleChange}
                            nomarg
                            type='number'
                        />
                        <OtherText grey>Higher gets workers and machines first</OtherText>
                    </Flex>
                    <Flex column width='100%'>
                        <HonestSelectInput
                            key={`cycle-degradeRule-${formKey}`}
                            options={cantKeepUpOptions}
                            labelText="If we can't keep up"
                            name='degradeRule'
                            onChange={(v: ISelectValue) => updateBindingConfig({ degradeRule: v })}
                            defaultValue={bindingConfig.degradeRule?.value}
                            nomarg
                        />
                        <OtherText grey>What the system does when this pace is not possible</OtherText>
                    </Flex>
                </Flex>
                <ThinSeparator />
                <ItemsGrid columns={2} >
                    {bindingConfig.componentsData?.map((c) => renderComponentInput(c))}
                </ItemsGrid>
            </>}
        </Flex>

    </>
}


interface IComponent {
    taskCycleTemplateComponent: ISelectValue;
    componentType: number;
    value: number | Date | ISelectValue | string
}

